import { isNil } from 'lodash-es';
import { convertValueWithUnit, ConvertValueToUnits } from '@/common/utils/convertUnits.utils';
import { numberWithComma, roundToDigitValue } from '@/common/utils/core/number/format';
import { convertTimeToSec } from '@/common/utils/commonUtils';
import type {
  GridCellInstance,
  GridHeaderInstance,
  GridColumnInstance,
  GridColumnMeta,
  GridRawRowDataType,
} from '../../modernGrid.types';

const isEmptyValue = (value: unknown): value is null | undefined | '' =>
  isNil(value) || value === '';

/**
 * Cell 관련 헬퍼 함수들을 제공하는 composable
 * Cell의 메타 정보 조회, 포맷팅, 렌더링 타입 확인 등의 유틸리티 함수를 제공
 */
export const useCellHelpers = <RowDataType extends GridRawRowDataType>() => {
  // 바이트 단위로 포맷팅된 값과 단위를 반환
  const getFormattedValueWithByteUnit = ({
    value,
    unit = 'byte',
    toUnit,
    decimal = 3,
  }: {
    value: number | null;
    unit?: string;
    toUnit?: string;
    decimal: number;
  }): string | null => {
    if (isEmptyValue(value)) {
      return null;
    }

    const { value: convertedValue, unit: convertedUnit } = convertValueWithUnit(
      { value, unit },
      { toUnit: toUnit as ConvertValueToUnits | undefined, decimal },
    );
    return `${numberWithComma(convertedValue)}${toUnit ? '' : ` ${convertedUnit}`}`;
  };

  // Cell의 컬럼 메타 정보를 반환
  const getCellColumnMeta = (cell: GridCellInstance<RowDataType>): GridColumnMeta =>
    (cell.column as GridColumnInstance<RowDataType>).columnDef.meta || {};

  // Header의 컬럼 메타 정보를 반환
  const getHeaderColumnMeta = (header: GridHeaderInstance<RowDataType>): GridColumnMeta =>
    (header.column as GridColumnInstance<RowDataType>).columnDef.meta || {};

  // Cell의 렌더러 타입을 반환
  const getCellRendererType = (cell: GridCellInstance<RowDataType>): string | undefined =>
    getCellColumnMeta(cell).rendererType;

  // Cell이 클릭 가능한지 여부를 반환
  const isCellClickable = (cell: GridCellInstance<RowDataType>): boolean =>
    !!getCellColumnMeta(cell).clickable;

  // 바이트 Cell의 타이틀을 반환
  const getByteCellTitle = (cell: GridCellInstance<RowDataType>, value: unknown): string => {
    if (isEmptyValue(value)) {
      return '';
    }

    const meta = getCellColumnMeta(cell);
    const unit = meta.attrs?.unit || 'byte';
    return `${numberWithComma(value as number)} ${unit}`;
  };

  // 포맷된 바이트 값을 반환
  const getFormattedByteValue = (
    cell: GridCellInstance<RowDataType>,
    value: unknown,
  ): string | null => {
    if (isEmptyValue(value)) {
      return null;
    }

    const meta = getCellColumnMeta(cell);
    return getFormattedValueWithByteUnit({
      value: value as number | null,
      unit: meta.attrs?.unit || 'byte',
      decimal: meta.decimal ?? 3,
    });
  };

  // 초 단위 Cell의 타이틀을 반환
  const getToSecCellTitle = (cell: GridCellInstance<RowDataType>, value: unknown): string => {
    if (isEmptyValue(value)) {
      return '';
    }

    const meta = getCellColumnMeta(cell);
    const unit = meta.attrs?.unit || '';
    return `${numberWithComma(value as number)} ${unit}`.trim();
  };

  // 초 단위 Cell의 값을 반환
  const getToSecCellValue = (
    cell: GridCellInstance<RowDataType>,
    value: unknown,
  ): string | number | null => {
    if (isEmptyValue(value)) {
      return null;
    }

    const meta = getCellColumnMeta(cell);
    return convertTimeToSec(value as number, meta.attrs?.unit, 'string');
  };

  // 숫자 Cell의 타이틀을 반환
  const getDigitCellTitle = (_cell: GridCellInstance<RowDataType>, value: unknown): string => {
    if (isEmptyValue(value)) {
      return '';
    }

    return numberWithComma(value as number);
  };

  // 숫자 Cell의 값을 반환
  const getDigitCellValue = (
    cell: GridCellInstance<RowDataType>,
    value: unknown,
  ): string | number | null => {
    if (isEmptyValue(value)) {
      return null;
    }

    const meta = getCellColumnMeta(cell);
    return roundToDigitValue(value as number, meta.decimal ?? 3);
  };

  return {
    getFormattedValueWithByteUnit,
    getCellColumnMeta,
    getHeaderColumnMeta,
    getCellRendererType,
    isCellClickable,
    getByteCellTitle,
    getFormattedByteValue,
    getToSecCellTitle,
    getToSecCellValue,
    getDigitCellTitle,
    getDigitCellValue,
  };
};
